import React, { useState, useEffect } from 'react';
import {
  FaLaptop,
  FaDesktop,
  FaBuilding,
  FaBarcode,
  FaMemory,
  FaHdd,
  FaExclamationTriangle,
  FaServer,
  FaTag,
  FaStickyNote,
  FaQrcode
} from 'react-icons/fa';
import PropTypes from 'prop-types';
import { Modal } from './common';
import axiosInstance from '../utils/axiosConfig';
import { getApiError } from '../utils/apiError';
import './FormModal.css';

function NewAssetModal({ onClose, onAssetAdded }) {
  const [formData, setFormData] = useState({
    tipo: '',
    sede_id: '',
    marca: '',
    modelo: '',
    serial: '',
    procesador: '',
    ram: '',
    disco: '',
    activo_fijo: '',
    notas: ''
  });

  const [sedes, setSedes] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [rams, setRams] = useState([]);
  const [discos, setDiscos] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const responses = await Promise.all([
          axiosInstance.get('/api/sedes'),
          axiosInstance.get('/api/marcas'),
          axiosInstance.get('/api/rams'),
          axiosInstance.get('/api/discos')
        ]);

        const [sedesData, marcasData, ramsData, discosData] = responses.map(r => r.data);

        setSedes(sedesData);
        setMarcas(marcasData);
        setRams(ramsData);
        setDiscos(discosData);
        setError(null);
      } catch (err) {
        setError('Error al cargar los datos necesarios');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await axiosInstance.post('/api/activos', formData);

      onAssetAdded(response.data.asset);
      onClose();
    } catch (error) {
      console.error('Error:', error);
      setError(getApiError(error, 'Error al guardar el equipo'));
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'activo_fijo') {
      const numbersOnly = value.replace(/\D/g, '').slice(0, 4);
      setFormData(prev => ({
        ...prev,
        [name]: numbersOnly
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getDeviceIcon = (tipo) => {
    switch(tipo?.toLowerCase()) {
      case 'laptop':
        return <FaLaptop className="device-icon laptop" />;
      case 'desktop':
        return <FaDesktop className="device-icon desktop" />;
      case 'aio':
        return <FaServer className="device-icon aio" />;
      default:
        return <FaLaptop className="device-icon" />;
    }
  };

  const getPreviewNombreEquipo = () => {
    if (!formData.sede_id || !formData.tipo || !formData.activo_fijo) {
      return '';
    }

    const sede = sedes.find(s => s.id === parseInt(formData.sede_id));
    if (!sede) return '';

    const sedesCodes = {
      'Caracas': 'CCS',
      'CA1': 'CA1',
      'CDN': 'CDN',
      'Foraneo': 'FOR'
    };

    const tiposCodes = {
      'LAPTOP': 'L',
      'AIO': 'A',
      'DESKTOP': 'D'
    };

    const sedeCode = sedesCodes[sede.nombre] || 'OTR';
    const tipoCode = tiposCodes[formData.tipo] || 'X';
    const activoNumber = formData.activo_fijo.padStart(4, '0');

    return `${sedeCode}-${tipoCode}${activoNumber}`;
  };

  if (loading) {
    return (
      <Modal isOpen={true} onClose={onClose} size="lg">
        <Modal.Body>
          <div className="loading-spinner">Cargando...</div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <Modal.Header onClose={onClose}>Nuevo Equipo</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} id="new-asset-form">
          <div className="form-modal-section">
            <h3><FaLaptop /> Información del Equipo</h3>
            <div className="form-modal-grid three-cols">
              <div className="form-modal-group">
                <label htmlFor="tipo">
                  <FaLaptop className="input-icon" /> Tipo de Equipo
                </label>
                <select
                  id="tipo"
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione un tipo</option>
                  <option value="AIO">All-in-One</option>
                  <option value="LAPTOP">Laptop</option>
                  <option value="DESKTOP">Desktop</option>
                </select>
              </div>

              <div className="form-modal-group">
                <label htmlFor="sede_id">
                  <FaBuilding className="input-icon" /> Sede
                </label>
                <select
                  id="sede_id"
                  name="sede_id"
                  value={formData.sede_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione una sede</option>
                  {sedes.map(sede => (
                    <option key={sede.id} value={sede.id}>
                      {sede.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-modal-group">
                <label htmlFor="activo_fijo">
                  <FaQrcode className="input-icon" /> Activo Fijo
                </label>
                <input
                  type="text"
                  id="activo_fijo"
                  name="activo_fijo"
                  value={formData.activo_fijo}
                  onChange={handleChange}
                  required
                  placeholder="Solo números (4 dígitos)"
                  maxLength="4"
                  pattern="\d{1,4}"
                />
                {formData.activo_fijo && (
                  <div className="form-modal-input-preview">
                    Nombre del equipo: <strong>{getPreviewNombreEquipo()}</strong>
                  </div>
                )}
              </div>

              <div className="form-modal-group">
                <label htmlFor="marca">
                  <FaTag className="input-icon" /> Marca
                </label>
                <select
                  id="marca"
                  name="marca"
                  value={formData.marca}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione una marca</option>
                  {marcas.map(marca => (
                    <option key={marca} value={marca}>
                      {marca}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-modal-group">
                <label htmlFor="modelo">
                  <FaTag className="input-icon" /> Modelo
                </label>
                <input
                  type="text"
                  id="modelo"
                  name="modelo"
                  value={formData.modelo}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Latitude 5420"
                />
              </div>

              <div className="form-modal-group">
                <label htmlFor="serial">
                  <FaBarcode className="input-icon" /> Serial
                </label>
                <input
                  type="text"
                  id="serial"
                  name="serial"
                  value={formData.serial}
                  onChange={handleChange}
                  required
                  placeholder="Número de serie"
                />
              </div>

              <div className="form-modal-group">
                <label htmlFor="procesador">
                  <FaServer className="input-icon" /> Procesador
                </label>
                <input
                  type="text"
                  id="procesador"
                  name="procesador"
                  value={formData.procesador}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Intel Core i5-1135G7"
                />
              </div>

              <div className="form-modal-group">
                <label htmlFor="ram">
                  <FaMemory className="input-icon" /> Memoria RAM
                </label>
                <select
                  id="ram"
                  name="ram"
                  value={formData.ram}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione RAM</option>
                  {rams.map(ram => (
                    <option key={ram} value={ram}>
                      {ram}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-modal-group">
                <label htmlFor="disco">
                  <FaHdd className="input-icon" /> Disco Duro
                </label>
                <select
                  id="disco"
                  name="disco"
                  value={formData.disco}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione Disco</option>
                  {discos.map(disco => (
                    <option key={disco} value={disco}>
                      {disco}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-modal-group span-full three-cols">
                <label htmlFor="notas">
                  <FaStickyNote className="input-icon" /> Notas
                </label>
                <textarea
                  id="notas"
                  name="notas"
                  value={formData.notas}
                  onChange={handleChange}
                  placeholder="Notas adicionales sobre el equipo (opcional)"
                  rows="3"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="form-modal-error">
              <FaExclamationTriangle />
              {error}
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </button>
        <button type="submit" form="new-asset-form" className="btn btn-primary" disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar Equipo'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

NewAssetModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onAssetAdded: PropTypes.func.isRequired,
};

export default NewAssetModal; 